/**
 * FixReceiptDialog — the Fix Receipt screen (Fix Receipt arc, Phase 3).
 *
 * The panel-approved one-pass correction flow: ONE describe → ONE
 * server-computed preview → ONE PIN → ONE handover. Backed by
 * POST /invoices/{id}/fix (Phase 2): preview and commit share the same
 * server code path, so every number on this screen ORIGINATES from the
 * preview response — this component does NO money math (ruling 7).
 *
 * Behavior contract (docs/audit/2026-08-24-fix-receipt-simulation.md §3):
 *   • Corrections are sent by line_index; additions ride the same product
 *     search the sales grid uses (never-encoded products welcome).
 *   • Pickup (partial-release) orders: qty fields greyed (ruling 5).
 *   • SI issued → the flow does not open: we explain and offer the legal
 *     route (void + re-encode with prefill, or returns-today + new
 *     receipt) straight from the server's structured refusal.
 *   • Closed-day originals: corrections only; the closed_day_fix
 *     credential (tenant Admin PIN or owner authenticator) is collected
 *     on the PIN step. Carryover explanation shown before commit.
 *   • Settle once: refund channel picker (drawer cash / digital send) or
 *     collect method picker; walk-ins must collect for the fix to save.
 *   • Standard PIN entry only (ruling 8) — no browser prompt().
 *   • payAmendDelta does not exist here: the endpoint settles exactly once.
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import DrawerTopUpPrompt from './DrawerTopUpPrompt';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import SmartProductSearch from './SmartProductSearch';
import {
  AlertTriangle, ArrowLeft, Banknote, CheckCircle2, Loader2, Lock,
  Package, RotateCcw, Send, Shield, Smartphone, Trash2, Wrench,
} from 'lucide-react';
import { toast } from 'sonner';

const COLLECT_METHODS = ['Cash', 'GCash', 'Maya', 'Bank Transfer'];

export default function FixReceiptDialog({
  open, onOpenChange, invoice, currentBranch,
  onDone,             // reload the parent after a committed fix
  onVoidReencode,     // SI route (day open): open the existing void+re-encode flow
  onOpenDiffSale,     // SI route (day closed) / paper-locked: start a new-receipt path
  prefill,            // optional {corrections, additions, reason} (guided walk-in path)
}) {
  const [step, setStep] = useState('describe'); // describe | preview | pin | done | si_locked
  const [busy, setBusy] = useState(false);
  const [lines, setLines] = useState([]);          // edited copies of invoice.items
  const [additions, setAdditions] = useState([]);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);    // server preview payload (the ONLY money source)
  const [siInfo, setSiInfo] = useState(null);      // structured si_locked detail
  const [collectMethod, setCollectMethod] = useState('Cash');
  const [collectRef, setCollectRef] = useState('');
  const [refundChannel, setRefundChannel] = useState('cash'); // 'cash' | 'digital'
  const [fixPin, setFixPin] = useState('');
  const [closedDayPin, setClosedDayPin] = useState('');
  const [commitResult, setCommitResult] = useState(null);
  // Empty-drawer remedy (owner-approved 2026-08-29): hold the server's
  // insufficient_funds payload so the cashier can top up from the Safe
  // in place and retry, instead of dead-ending mid-correction.
  const [fundsShort, setFundsShort] = useState(null);
  const [reasonTouched, setReasonTouched] = useState(false);

  const isPickup = invoice?.release_mode === 'partial';

  // Reset ONLY when the dialog opens. The parent reloads the invoice after a
  // committed fix (onDone), and that new `invoice` identity must NOT clobber
  // the handover screen back to 'describe' (caught in the live click-through).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open || !invoice) return;
    const base = (invoice.items || []).map(it => ({
      product_id: it.product_id, product_name: it.product_name || '',
      unit: it.unit || '', orig_qty: parseFloat(it.quantity || 0),
      orig_rate: parseFloat(it.rate || 0),
      quantity: String(it.quantity ?? ''), rate: String(it.rate ?? ''),
    }));
    if (prefill?.corrections?.length) {
      prefill.corrections.forEach(c => {
        const row = base[c.line_index];
        if (!row) return;
        if (c.quantity !== undefined && c.quantity !== null) row.quantity = String(c.quantity);
        if (c.rate !== undefined && c.rate !== null) row.rate = String(c.rate);
      });
    }
    setLines(base);
    setAdditions(prefill?.additions?.map(a => ({
      ...a, quantity: String(a.quantity ?? ''), rate: String(a.rate ?? ''),
    })) || []);
    setReason(prefill?.reason || '');
    setStep('describe'); setPreview(null); setSiInfo(null);
    setFixPin(''); setClosedDayPin(''); setCollectMethod('Cash');
    setCollectRef(''); setRefundChannel('cash'); setCommitResult(null);
    setFundsShort(null); setReasonTouched(false);
  }, [open]);

  // Corrections = per-line diffs vs the original (line_index matching).
  const corrections = useMemo(() => {
    const out = [];
    lines.forEach((l, idx) => {
      const q = parseFloat(l.quantity), r = parseFloat(l.rate);
      // Send product_id so the server can PROVE this correction still points
      // at the line the cashier saw (sweep 2026-08-29). Position alone is not
      // identity: a spliced array or a stale prefill would otherwise become a
      // silent wrong-SKU correction that moves real stock and money.
      const c = { line_index: idx, product_id: l.product_id };
      let changed = false;
      if (!isNaN(q) && Math.abs(q - l.orig_qty) > 1e-9) { c.quantity = q; changed = true; }
      if (!isNaN(r) && Math.abs(r - l.orig_rate) > 0.0049) { c.rate = r; changed = true; }
      if (changed) out.push(c);
    });
    return out;
  }, [lines]);

  const additionsPayload = useMemo(() => additions
    .filter(a => a.product_id && parseFloat(a.quantity) > 0)
    .map(a => ({
      product_id: a.product_id, product_name: a.product_name,
      quantity: parseFloat(a.quantity), rate: parseFloat(a.rate) || 0,
      unit: a.unit || '', is_repack: !!a.is_repack,
      vat_class: a.vat_class || 'vatable',
    })), [additions]);

  const settlePayload = () => {
    const s = {};
    if (preview?.settle?.direction === 'collect') {
      s.method = collectMethod;
      if (collectRef.trim()) s.reference = collectRef.trim();
    } else if (preview?.settle?.direction === 'refund') {
      // ALWAYS declare the channel the cashier picked (live-review B1,
      // 2026-08-29). Sending nothing used to fall back to pool-driven
      // routing, so picking "Drawer cash" on a GCash-paid receipt debited
      // the DIGITAL wallet while cash left the drawer — the opposite of
      // what this screen promises.
      s.refund_channel = refundChannel;
      if (refundChannel === 'digital') s.method = 'GCash';
    }
    return s;
  };

  // Live-review B6 (2026-08-29): every server refusal must reach the screen
  // in the server's own words, and only a real credential rejection may send
  // the user back to the PIN step. The old commit handler caught ALL 403s,
  // threw away structured `detail` objects (shift/insufficient-funds errors
  // carry `code`/`type`, not `error`), printed "Authorization refused" and
  // parked the user at a PIN box — blaming the manager PIN for problems that
  // had nothing to do with it.
  const handleServerError = (e, { pinStepOnAuthFailure = false } = {}) => {
    const detail = e.response?.data?.detail;
    const status = e.response?.status;
    if (detail && typeof detail === 'object') {
      if (detail.error === 'si_locked') { setSiInfo(detail); setStep('si_locked'); return; }
      if (detail.error === 'walkin_settle_required') {
        toast.error(detail.message); setStep('preview'); return;
      }
      if (detail.error === 'fix_pin_required' || detail.error === 'closed_day_fix_pin_required') {
        toast.error(detail.message); setStep('pin'); return;
      }
      // Empty drawer → offer the Safe top-up in place rather than ending
      // the correction (the cashier is standing in front of a customer).
      if (detail.type === 'insufficient_funds') {
        setFundsShort(detail);
        setStep('preview');
        return;
      }
      // Structured errors from shared helpers (shift_required,
      // subscription…) — surface verbatim, stay put.
      toast.error(detail.message || 'Fix failed', { duration: 9000 });
      if (detail.code || detail.type) setStep('preview');
      return;
    }
    const msg = typeof detail === 'string' ? detail : 'Fix failed';
    toast.error(msg, { duration: 9000 });
    if (pinStepOnAuthFailure && status === 403 && /\bpin\b|authoriz/i.test(msg)) {
      setStep('pin');
    }
  };

  const runPreview = async () => {
    if (corrections.length === 0 && additionsPayload.length === 0) {
      toast.error('Nothing changed yet — correct a line or add an item first.');
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/invoices/${invoice.id}/fix`, {
        mode: 'preview', corrections, additions: additionsPayload,
      });
      setPreview(res.data);
      setStep('preview');
    } catch (e) { handleServerError(e); }
    setBusy(false);
  };

  const needsPin = preview?.requires?.fix_pin || preview?.requires?.closed_day_pin;

  const runCommit = async () => {
    // Inline validation (owner 2026-08-29: the toast flashed by too fast to
    // read — a required field must say so ON the field).
    if (!reason.trim()) { setReasonTouched(true); setStep('describe'); return; }
    setBusy(true);
    try {
      const body = {
        mode: 'commit', corrections, additions: additionsPayload,
        reason: reason.trim(), settle: settlePayload(),
      };
      if (fixPin.trim()) body.pin = fixPin.trim();
      if (closedDayPin.trim()) body.closed_day_pin = closedDayPin.trim();
      // Commit-once: echo the version the PREVIEW was computed from, so a
      // replayed submit (lost response, retry) cannot apply the correction
      // a second time (sweep 2026-08-29).
      if (preview?.version) body.if_version = preview.version;
      // Commit-bound-to-preview (Phase B brief item 4): echo the settle the
      // operator SAW — the server 409s if the money side moved.
      if (preview?.settle) {
        body.expected_settle = {
          direction: preview.settle.direction,
          amount: preview.settle.amount,
        };
      }
      const res = await api.post(`/invoices/${invoice.id}/fix`, body);
      setCommitResult(res.data);
      setStep('done');
      onDone?.();
    } catch (e) {
      handleServerError(e, { pinStepOnAuthFailure: true });
    }
    setBusy(false);
  };

  const confirmFromPreview = () => {
    if (preview?.requires?.walkin_collect && preview?.settle?.direction === 'collect'
        && !collectMethod) {
      toast.error('Pick a payment method — the collect must complete for this fix to save.');
      return;
    }
    if (needsPin) setStep('pin'); else runCommit();
  };

  const addProduct = (p) => {
    const rate = p.prices?.retail ?? p.price ?? 0;
    setAdditions(prev => [...prev, {
      product_id: p.id, product_name: p.name, unit: p.unit || '',
      is_repack: !!p.is_repack, vat_class: p.vat_class || 'vatable',
      quantity: '1', rate: String(rate),
    }]);
  };

  if (!invoice) return null;
  const num = invoice.invoice_number || '';

  return (
    <Dialog open={open} onOpenChange={v => { if (!busy) onOpenChange?.(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="fix-receipt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A4D2E]">
            <Wrench size={18} /> Fix Receipt — {num}
          </DialogTitle>
        </DialogHeader>

        {/* ── SI locked: explain + legal route ─────────────────────────── */}
        {step === 'si_locked' && siInfo && (
          <div className="space-y-3" data-testid="fix-si-locked">
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800 flex gap-2">
              <Lock size={16} className="shrink-0 mt-0.5" />
              <span>{siInfo.message}</span>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => onOpenChange?.(false)}>Close</Button>
              {siInfo.route === 'void_reencode' && (
                <Button className="bg-[#1A4D2E] hover:bg-[#143d24]"
                  onClick={() => { onOpenChange?.(false); onVoidReencode?.(siInfo); }}
                  data-testid="fix-si-void-reencode">
                  Void (incl. BIR Receipt) &amp; Re-encode
                </Button>
              )}
              {siInfo.route === 'returns_plus_new_receipt' && (
                <Button className="bg-[#1A4D2E] hover:bg-[#143d24]"
                  onClick={() => { onOpenChange?.(false); onOpenDiffSale?.(siInfo); }}
                  data-testid="fix-si-new-receipt">
                  Return today / New receipt
                </Button>
              )}
              {/* payment_adjustment_cor (price-delta-only on a locked receipt):
                  the COR document arrives with the Correction Center (P2b) —
                  the message explains the interim manual path; no false door. */}
            </div>
          </div>
        )}

        {/* ── STEP 1: describe the final truth ─────────────────────────── */}
        {step === 'describe' && (
          <div className="space-y-4" data-testid="fix-describe-step">
            <p className="text-xs text-slate-500">
              Set every line to what the receipt <b>should say</b>. The server
              computes the money — you'll see one preview and settle once.
            </p>
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-1.5">Item</th>
                    <th className="text-right px-2 py-1.5 w-24">Qty</th>
                    <th className="text-right px-2 py-1.5 w-28">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => (
                    <tr key={idx} className="border-t border-slate-100">
                      <td className="px-3 py-1.5">
                        {l.product_name}
                        <span className="text-xs text-slate-400 ml-1">
                          (was {l.orig_qty} × {formatPHP(l.orig_rate)})
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        <Input type="number" inputMode="decimal" className="h-8 text-right"
                          value={l.quantity} disabled={isPickup}
                          title={isPickup ? 'Pickup order — quantities are final' : undefined}
                          onChange={e => setLines(ls => ls.map((x, i) =>
                            i === idx ? { ...x, quantity: e.target.value } : x))}
                          data-testid={`fix-qty-${idx}`} />
                      </td>
                      <td className="px-2 py-1.5 flex items-center gap-1">
                        <Input type="number" inputMode="decimal" className="h-8 text-right"
                          value={l.rate}
                          onChange={e => setLines(ls => ls.map((x, i) =>
                            i === idx ? { ...x, rate: e.target.value } : x))}
                          data-testid={`fix-rate-${idx}`} />
                        {/* Remove this line (owner 2026-08-29). This sets the
                            quantity to ZERO — it never deletes the row. The
                            zero line stays on the receipt and in the journal
                            on purpose: "PARAPEST 0" is the evidence that the
                            item was taken off, and the line positions the
                            correction refers to must not shift. */}
                        {parseFloat(l.quantity) === 0 ? (
                          <button type="button" className="text-slate-400 hover:text-emerald-700 shrink-0"
                            disabled={isPickup}
                            onClick={() => setLines(ls => ls.map((x, i) =>
                              i === idx ? { ...x, quantity: String(x.orig_qty) } : x))}
                            title="Put this item back on the receipt"
                            data-testid={`fix-restore-${idx}`}>
                            <RotateCcw size={14} />
                          </button>
                        ) : (
                          <button type="button" className="text-slate-400 hover:text-red-600 shrink-0 disabled:opacity-30"
                            disabled={isPickup}
                            onClick={() => setLines(ls => ls.map((x, i) =>
                              i === idx ? { ...x, quantity: '0' } : x))}
                            title={isPickup
                              ? 'Pickup order — quantities are final'
                              : 'Remove this item from the receipt (kept as a zero line for the trail)'}
                            data-testid={`fix-remove-${idx}`}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {additions.map((a, idx) => (
                    <tr key={`add-${idx}`} className="border-t border-emerald-100 bg-emerald-50/40">
                      <td className="px-3 py-1.5">
                        <span className="text-emerald-700 font-medium">{a.product_name}</span>
                        <span className="text-[10px] text-emerald-600 ml-1 uppercase">added</span>
                      </td>
                      <td className="px-2 py-1.5">
                        <Input type="number" inputMode="decimal" className="h-8 text-right"
                          value={a.quantity}
                          onChange={e => setAdditions(ad => ad.map((x, i) =>
                            i === idx ? { ...x, quantity: e.target.value } : x))} />
                      </td>
                      <td className="px-2 py-1.5 flex items-center gap-1">
                        <Input type="number" inputMode="decimal" className="h-8 text-right"
                          value={a.rate}
                          onChange={e => setAdditions(ad => ad.map((x, i) =>
                            i === idx ? { ...x, rate: e.target.value } : x))} />
                        <button className="text-slate-400 hover:text-red-600"
                          onClick={() => setAdditions(ad => ad.filter((_, i) => i !== idx))}
                          title="Remove added line">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {isPickup ? (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <AlertTriangle size={12} /> Pickup order — quantities are final; price corrections only.
              </p>
            ) : (
              <div className="space-y-1">
                <Label className="text-xs text-slate-500 flex items-center gap-1">
                  <Package size={12} /> Add items (products never on this receipt are fine)
                </Label>
                <SmartProductSearch
                  branchId={invoice.branch_id || currentBranch?.id}
                  onSelect={addProduct}
                  onCreateNew={() => {}}
                  placeholder="Search a product to add…"
                />
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs text-slate-500">
                Why is this receipt being fixed? <span className="text-red-500">*</span>
              </Label>
              <Textarea rows={2} value={reason}
                onChange={e => { setReason(e.target.value); if (e.target.value.trim()) setReasonTouched(false); }}
                onBlur={() => setReasonTouched(true)}
                className={reasonTouched && !reason.trim() ? 'border-red-400 focus-visible:ring-red-400' : ''}
                placeholder="e.g. Stock short on hog mash — 2 sacks not given; customer added 1 bag urea"
                data-testid="fix-reason" />
              {/* Owner 2026-08-29: this used to be a toast that flashed past
                  before it could be read. A required field says so in place. */}
              {reasonTouched && !reason.trim() && (
                <p className="text-[11px] text-red-600" data-testid="fix-reason-error">
                  A fix reason is required — it is printed on the correction paper.
                </p>
              )}
            </div>

            <p className="text-[11px] text-slate-400">
              If an item was never on the shelf, this fixes the receipt — not the stock count.
            </p>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange?.(false)}>Cancel</Button>
              <Button className="bg-[#1A4D2E] hover:bg-[#143d24]" onClick={runPreview}
                disabled={busy} data-testid="fix-preview-btn">
                {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                Preview fix
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 2: the ONE server preview ───────────────────────────── */}
        {step === 'preview' && preview && (
          <div className="space-y-4" data-testid="fix-preview-step">
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 text-sm">
              {(preview.changes || []).map((c, i) => (
                <div key={i} className="px-3 py-1.5 text-slate-600">{c}</div>
              ))}
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">New total</span><b>{formatPHP(preview.grand_total)}</b></div>
              <div className="flex justify-between"><span className="text-slate-500">Already paid</span><b>{formatPHP(preview.amount_paid)}</b></div>
              <div className="flex justify-between border-t border-slate-200 pt-1">
                {preview.settle.direction === 'refund' && (
                  <><span className="font-semibold text-amber-700">Refund the customer</span>
                    <b className="text-amber-700" data-testid="fix-settle-amount">{formatPHP(preview.settle.amount)}</b></>
                )}
                {preview.settle.direction === 'collect' && (
                  <><span className="font-semibold text-emerald-700">Collect from the customer</span>
                    <b className="text-emerald-700" data-testid="fix-settle-amount">{formatPHP(preview.settle.amount)}</b></>
                )}
                {preview.settle.direction === 'even' && (
                  <><span className="font-semibold text-slate-600">Even — no money moves</span>
                    <b data-testid="fix-settle-amount">{formatPHP(0)}</b></>
                )}
              </div>
            </div>

            {preview.paper_route === 'closed_day_carryover' && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex gap-2">
                <Lock size={14} className="shrink-0 mt-0.5" />
                <span>
                  This receipt's business day is already Z-closed. The sealed report
                  stays untouched — the adjustment posts on <b>today's</b> records,
                  labeled as a carryover. This needs the tenant Admin PIN or the
                  owner's authenticator code.
                </span>
              </div>
            )}

            {preview.settle.direction === 'collect' && (
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">
                  {preview.requires.walkin_collect
                    ? 'Walk-in receipt — the collect must complete for this fix to save:'
                    : 'Collect now (or leave on the customer’s account by cancelling):'}
                </Label>
                <div className="flex gap-1.5 flex-wrap">
                  {COLLECT_METHODS.map(m => (
                    <Button key={m} size="sm"
                      variant={collectMethod === m ? 'default' : 'outline'}
                      className={collectMethod === m ? 'bg-[#1A4D2E] hover:bg-[#143d24]' : ''}
                      onClick={() => setCollectMethod(m)}
                      data-testid={`fix-method-${m.replace(/\s/g, '')}`}>
                      {m === 'Cash' ? <Banknote size={13} className="mr-1" /> : <Smartphone size={13} className="mr-1" />}
                      {m}
                    </Button>
                  ))}
                </div>
                {collectMethod !== 'Cash' && (
                  <Input className="h-8" placeholder="Reference no. (optional)"
                    value={collectRef} onChange={e => setCollectRef(e.target.value)} />
                )}
              </div>
            )}

            {preview.settle.direction === 'refund' && (
              <div className="space-y-2">
                <Label className="text-xs text-slate-500">How is the money physically handed back?</Label>
                <div className="flex gap-1.5">
                  <Button size="sm" variant={refundChannel === 'cash' ? 'default' : 'outline'}
                    className={refundChannel === 'cash' ? 'bg-[#1A4D2E] hover:bg-[#143d24]' : ''}
                    onClick={() => setRefundChannel('cash')} data-testid="fix-refund-cash">
                    <Banknote size={13} className="mr-1" /> Drawer cash
                  </Button>
                  <Button size="sm" variant={refundChannel === 'digital' ? 'default' : 'outline'}
                    className={refundChannel === 'digital' ? 'bg-[#1A4D2E] hover:bg-[#143d24]' : ''}
                    onClick={() => setRefundChannel('digital')} data-testid="fix-refund-digital">
                    <Send size={13} className="mr-1" /> Send digitally (GCash)
                  </Button>
                </div>
                <p className="text-[11px] text-slate-400">
                  The wallet debited matches what you actually hand back.
                </p>
              </div>
            )}

            {/* Empty drawer → the remedy, in place (owner 2026-08-29). */}
            {fundsShort && (
              <DrawerTopUpPrompt
                detail={fundsShort}
                branchId={invoice?.branch_id || currentBranch?.id}
                linkRef={invoice?.invoice_number || ''}
                onFunded={() => { setFundsShort(null); runCommit(); }}
                onCancel={() => setFundsShort(null)}
              />
            )}

            <div className="flex justify-between gap-2">
              <Button variant="outline" onClick={() => setStep('describe')}>
                <ArrowLeft size={14} className="mr-1" /> Back
              </Button>
              <Button className="bg-[#1A4D2E] hover:bg-[#143d24]" onClick={confirmFromPreview}
                disabled={busy || !!fundsShort} data-testid="fix-confirm-btn">
                {needsPin ? <Shield size={14} className="mr-1" /> : null}
                {needsPin ? 'Authorize & commit' : 'Commit fix'}
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 3: the ONE PIN screen (standard entry, ruling 8) ────── */}
        {step === 'pin' && preview && (
          <div className="space-y-4" data-testid="fix-pin-step">
            {preview.requires.fix_pin && (
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600 flex items-center gap-1">
                  <Shield size={12} /> Manager authorization
                  <span className="text-slate-400">— {(preview.requires.fix_pin_reasons || []).join(', ')}</span>
                </Label>
                <Input type="password" autoFocus className="h-9"
                  placeholder="PIN or 6-digit authenticator code"
                  value={fixPin} onChange={e => setFixPin(e.target.value)}
                  data-testid="fix-pin-input" />
                <div className="flex gap-1.5">
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 font-medium">Admin PIN</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700 font-medium">Manager PIN</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-50 border border-purple-200 text-purple-700 font-medium">TOTP</span>
                </div>
              </div>
            )}
            {preview.requires.closed_day_pin && (
              <div className="space-y-1.5">
                <Label className="text-xs text-amber-700 flex items-center gap-1">
                  <Lock size={12} /> Closed-day authorization (higher bar)
                </Label>
                <Input type="password" className="h-9"
                  placeholder="Tenant Admin PIN or owner's authenticator code"
                  value={closedDayPin} onChange={e => setClosedDayPin(e.target.value)}
                  data-testid="fix-closed-day-pin-input" />
                <div className="flex gap-1.5">
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 font-medium">Admin PIN</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-50 border border-purple-200 text-purple-700 font-medium">Owner TOTP</span>
                </div>
                <p className="text-[11px] text-slate-400">A manager's PIN or authenticator will not pass this gate.</p>
              </div>
            )}
            <div className="flex justify-between gap-2">
              <Button variant="outline" onClick={() => setStep('preview')}>
                <ArrowLeft size={14} className="mr-1" /> Back
              </Button>
              <Button className="bg-[#1A4D2E] hover:bg-[#143d24]" onClick={runCommit}
                disabled={busy} data-testid="fix-commit-btn">
                {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCircle2 size={14} className="mr-1" />}
                Commit fix
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 4: handover ─────────────────────────────────────────── */}
        {step === 'done' && commitResult && (
          <div className="space-y-4" data-testid="fix-done-step">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800 flex gap-2">
              <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
              <div>
                <b>Receipt fixed.</b>
                {commitResult.settle_result?.direction === 'refund' && (
                  <div>Hand the customer <b>{formatPHP(commitResult.settle_result.amount)}</b>
                    {commitResult.settle_result.sources?.length
                      ? <span className="text-emerald-700"> (from: {commitResult.settle_result.sources.join(', ')})</span> : null}.
                  </div>
                )}
                {commitResult.settle_result?.direction === 'collect' && !commitResult.settle_result?.absorbed_into_ar && (
                  <div>Collected <b>{formatPHP(commitResult.settle_result.amount)}</b> via {commitResult.settle_result.method}.
                    {commitResult.settle_result.receipt_status === 'pending' &&
                      <span> Upload the payment screenshot when received.</span>}
                  </div>
                )}
                {commitResult.settle_result?.absorbed_into_ar && (
                  <div>{formatPHP(commitResult.settle_result.amount)} added to the customer's account balance.</div>
                )}
                {commitResult.settle_result?.direction === 'even' && <div>No money moved.</div>}
                <div className="mt-1 text-xs text-emerald-700">Reprint the receipt — it is the refund/settle paper.</div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button className="bg-[#1A4D2E] hover:bg-[#143d24]"
                onClick={() => onOpenChange?.(false)} data-testid="fix-done-btn">Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
